import { Link } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Ticket, Smartphone, Banknote, Send } from "lucide-react";
import { proofWhatsAppUrl } from "@/components/PaymentMethodPicker";
import { motion } from "framer-motion";
import { useLanguage } from "@/contexts/LanguageContext";

export default function BookingConfirmation({ open, onClose, booking, club, kind = "court" }) {
  const { t } = useLanguage();
  if (!booking) return null;
  const amount = kind === "court" ? booking.total_price : booking.price;
  const isJuice = booking.payment_method === "juice";
  const juiceNumber = club?.juice_number || "000050048236";
  const waShown = club?.whatsapp || "+230 57299188";
  const [y, m, d] = booking.date.split("-");
  const niceDate = `${d}-${m}-${y}`;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="booking-confirmation">
        <DialogHeader>
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", delay: 0.1 }} className="w-14 h-14 rounded-full bg-teal/15 text-teal flex items-center justify-center mx-auto mb-2">
            <CheckCircle2 size={30} />
          </motion.div>
          <DialogTitle className="text-center font-heading text-xl">{t("confirm.title")}</DialogTitle>
          <DialogDescription className="text-center">
            {kind === "coaching" && <>{t("confirm.lesson_with", { coach: booking.coach_name })} · </>}
            {niceDate} · <span className="text-golden font-semibold">{booking.start_time}–{booking.end_time}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-2xl border border-border/50 bg-muted/20 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground uppercase tracking-wider">{t("confirm.reference")}</span>
            <span className="font-mono font-bold text-golden" data-testid="booking-reference">{booking.reference}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground uppercase tracking-wider">{t("confirm.amount")}</span>
            <span className="font-black text-lg">Rs {Number(amount).toLocaleString()}</span>
          </div>
          <div className="pt-3 border-t border-border/40 flex gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${isJuice ? "bg-coral/15 text-coral" : "bg-golden/15 text-golden"}`}>
              {isJuice ? <Smartphone size={16} /> : <Banknote size={16} />}
            </div>
            <div className="text-sm leading-snug">
              {isJuice ? (
                <>
                  <p className="font-semibold">{t("confirm.pay_juice")}</p>
                  <ol className="text-muted-foreground text-xs mt-1 space-y-1.5 list-decimal pl-4">
                    <li>{t("confirm.step1a")} <span className="font-semibold text-foreground">Rs {Number(amount).toLocaleString()}</span> {t("confirm.step1b")} <span className="font-mono font-semibold text-foreground" data-testid="juice-number">{juiceNumber}</span>
                      <span className="block text-[10px] text-foreground mt-0.5">{t("confirm.reference")} <span className="font-mono font-semibold">{booking.reference}</span></span>
                    </li>
                    <li className="whitespace-nowrap">{t("confirm.step2")} <span className="text-[10px] text-foreground">({waShown})</span>.</li>
                    <li>{t("confirm.step3")}</li>
                  </ol>
                </>
              ) : (
                <>
                  <p className="font-semibold">{t("confirm.pay_cash")}</p>
                  <p className="text-muted-foreground text-xs mt-0.5">{t("confirm.cash_desc_a")} <span className="font-semibold text-foreground">Rs {Number(amount).toLocaleString()}</span> {t("confirm.cash_desc_b")}</p>
                </>
              )}
            </div>
          </div>
        </div>

        <p className="text-[11px] text-muted-foreground text-center">{t("confirm.policy")}</p>
        {isJuice ? (
          <a href={proofWhatsAppUrl(club, booking, kind)} target="_blank" rel="noreferrer" className="block">
            <Button className="w-full rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white gap-1.5 h-11 text-base" data-testid="send-proof-whatsapp-btn">
              <Send size={16} /> {t("confirm.send_proof")}
            </Button>
          </a>
        ) : (
          <Link to="/bookings" className="block" onClick={onClose}>
            <Button variant="outline" className="w-full rounded-full gap-1.5 h-11" data-testid="confirmation-my-bookings-btn"><Ticket size={15} /> {t("confirm.my_bookings")}</Button>
          </Link>
        )}
      </DialogContent>
    </Dialog>
  );
}
